import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  type EventFormData,
  emptyEventForm,
  Field,
  toPayload,
} from './EventForm';

export function CreateEventDialog() {
  const [open, setOpen] = useState(false);
  const form = useForm<EventFormData>(emptyEventForm);
  const { data, setData, processing, reset, transform } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform(toPayload);
    form.post('/admin/events', {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Create Event
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
            <DialogDescription>
              Create a new hackathon event with dates and capacity.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Event Name" error={errors.name}>
              <Input
                placeholder="Platanus Hack 2025"
                disabled={processing}
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
              />
            </Field>

            <Field label="Slug" error={errors.slug}>
              <Input
                placeholder="25"
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
            </Field>

            <Field label="Domain (with protocol)" error={errors.domain}>
              <Input
                placeholder="https://hack.platan.us"
                disabled={processing}
                value={data.domain}
                onChange={(e) => setData('domain', e.target.value)}
              />
            </Field>

            <Field label="Photos Album URL" error={errors.photosAlbumUrl}>
              <Input
                type="url"
                placeholder="https://photos.app.goo.gl/..."
                disabled={processing}
                value={data.photosAlbumUrl}
                onChange={(e) => setData('photosAlbumUrl', e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Priority Answer Date"
                error={errors.priorityAnswerDate}
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.priorityAnswerDate}
                  onChange={(e) =>
                    setData('priorityAnswerDate', e.target.value)
                  }
                />
              </Field>

              <Field label="Priority Deadline" error={errors.priorityDeadlineAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.priorityDeadlineAt}
                  onChange={(e) =>
                    setData('priorityDeadlineAt', e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Final Deadline" error={errors.finalDeadlineAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.finalDeadlineAt}
                  onChange={(e) => setData('finalDeadlineAt', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Starts At" error={errors.startsAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.startsAt}
                  onChange={(e) => setData('startsAt', e.target.value)}
                />
              </Field>

              <Field label="Ends At" error={errors.endsAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.endsAt}
                  onChange={(e) => setData('endsAt', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Hacking Ends At"
                error={errors.hackingEndsAt}
                help="When coding stops (drives the hacker dashboard countdown). Usually before Ends At."
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.hackingEndsAt}
                  onChange={(e) => setData('hackingEndsAt', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="RSVP Opens At" error={errors.rsvpOpenAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.rsvpOpenAt}
                  onChange={(e) => setData('rsvpOpenAt', e.target.value)}
                />
              </Field>

              <Field
                label="Track Selection Starts At"
                error={errors.trackSelectionStartTime}
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.trackSelectionStartTime}
                  onChange={(e) =>
                    setData('trackSelectionStartTime', e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Track Selection Ends At"
                error={errors.trackSelectionEndTime}
                help="Teams can freely change their track until this time."
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.trackSelectionEndTime}
                  onChange={(e) =>
                    setData('trackSelectionEndTime', e.target.value)
                  }
                />
              </Field>

              <Field
                label="Mentor Selection Ends At"
                error={errors.mentorSelectionEndTime}
                help="Teams can freely change their mentor until this time."
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.mentorSelectionEndTime}
                  onChange={(e) =>
                    setData('mentorSelectionEndTime', e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Voting Starts At" error={errors.votingStartsAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.votingStartsAt}
                  onChange={(e) => setData('votingStartsAt', e.target.value)}
                />
              </Field>

              <Field label="Voting Ends At" error={errors.votingEndsAt}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.votingEndsAt}
                  onChange={(e) => setData('votingEndsAt', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Mentor Selection Starts At"
                error={errors.mentorSelectionStartTime}
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.mentorSelectionStartTime}
                  onChange={(e) =>
                    setData('mentorSelectionStartTime', e.target.value)
                  }
                />
              </Field>

              <Field
                label="Feedback Prize Deadline"
                error={errors.feedbackPrizeDeadline}
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.feedbackPrizeDeadline}
                  onChange={(e) =>
                    setData('feedbackPrizeDeadline', e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <Field label="Capacity (Teams)" error={errors.capacityTeams}>
                <Input
                  type="number"
                  placeholder="50"
                  disabled={processing}
                  value={data.capacityTeams}
                  onChange={(e) => setData('capacityTeams', e.target.value)}
                />
              </Field>

              <Field label="Capacity (Hackers)" error={errors.capacityHackers}>
                <Input
                  type="number"
                  placeholder="200"
                  disabled={processing}
                  value={data.capacityHackers}
                  onChange={(e) => setData('capacityHackers', e.target.value)}
                />
              </Field>

              <Field label="Target Submission" error={errors.targetSubmission}>
                <Input
                  type="number"
                  placeholder="100"
                  disabled={processing}
                  value={data.targetSubmission}
                  onChange={(e) => setData('targetSubmission', e.target.value)}
                />
              </Field>

              <Field label="Track Team Limit" error={errors.trackTeamLimit}>
                <Input
                  type="number"
                  placeholder="10"
                  disabled={processing}
                  value={data.trackTeamLimit}
                  onChange={(e) => setData('trackTeamLimit', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <Field label="Mentor Team Limit" error={errors.mentorTeamLimit}>
                <Input
                  type="number"
                  placeholder="5"
                  disabled={processing}
                  value={data.mentorTeamLimit}
                  onChange={(e) => setData('mentorTeamLimit', e.target.value)}
                />
              </Field>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div>
                <h3 className="font-semibold text-sm">Discord</h3>
                <p className="text-muted-foreground text-xs">
                  Per-event Discord server config. Leave empty if this event
                  doesn't use Discord.
                </p>
              </div>
              <Field label="Guild ID" error={errors.discordGuildId}>
                <Input
                  placeholder="123456789012345678"
                  disabled={processing}
                  value={data.discordGuildId}
                  onChange={(e) => setData('discordGuildId', e.target.value)}
                />
              </Field>
              <Field
                label="Welcome Channel ID"
                error={errors.discordWelcomeChannelId}
              >
                <Input
                  placeholder="123456789012345678"
                  disabled={processing}
                  value={data.discordWelcomeChannelId}
                  onChange={(e) =>
                    setData('discordWelcomeChannelId', e.target.value)
                  }
                />
              </Field>
              <Field label="FAQ Channel ID" error={errors.discordFaqChannelId}>
                <Input
                  placeholder="123456789012345678"
                  disabled={processing}
                  value={data.discordFaqChannelId}
                  onChange={(e) =>
                    setData('discordFaqChannelId', e.target.value)
                  }
                />
              </Field>
              <Field
                label="Music Channel ID"
                error={errors.discordMusicChannelId}
              >
                <Input
                  placeholder="123456789012345678"
                  disabled={processing}
                  value={data.discordMusicChannelId}
                  onChange={(e) =>
                    setData('discordMusicChannelId', e.target.value)
                  }
                />
              </Field>
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create Event'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
